import { useEffect, useRef, useState } from 'react';
import { Loader, Group, Table } from '@mantine/core';
import Header from '../../../components/util/header';
import { useSelector } from 'react-redux';
import { fetchPOSEquipmentDetails, IAPMPosEquipment } from '../../../../libraries/apm';
import { getLocationName, handleEntid, formatShortTransactionType } from '../../../utils/script';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import PaginationComponent from '../../../components/pagination';
import EditEquipmentPos from '../../../components/edit-equipment-pos/edit-equipment-pos';
import { LoginState } from '../../../client-redux/login/reducer';
import { IEntity } from '../../../../libraries/entity';
import FilterBar, { FilterBarFieldsData } from '../../../components/filterbar';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeLabel } from '../../../utils/date';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

const APMEquipments = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');
  const controller = useRef(new AbortController()).current;
  const entities = useSelector<AppState, IEntity[]>(state => state.entities.entities);

  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [list, setList] = useState<IAPMPosEquipment[]>([]);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadApmEquipList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadApmEquipList = () => {
    if (!filterBarData || !limit) {
      return;
    }

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchPOSEquipmentDetails(
      controller,
      filterBarData?.entity || loginInfo.user?.entid || 0,
      filterBarData?.lid || 0,
      filterBarData?.posid || 0,
      offset,
      limit,
      true
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.poss || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const checkStatusForCell = (statusCode?: number | null) => {
    if (statusCode !== undefined && statusCode !== null) {
      if (statusCode === 4) {
        return <div className="offlineStatus">Offline</div>;
      } else if (statusCode === 0) {
        return '';
      } else {
        return <div className="onlineStatus">Online</div>;
      }
    }
  };

  const checkEquipmentEid = (eid?: number | null) => {
    if (eid !== undefined && eid !== null && eid > 0) {
      return eid;
    } else {
      return '';
    }
  };

  const checkTimeOpenCloseforCell = (timeCode?: number | null) => {
    if (timeCode !== undefined && timeCode !== null && timeCode > 0) {
      const hours = Math.floor(timeCode / 60);
      const minutes = timeCode % 60;
      const formattedHours = hours.toString().padStart(2, '00');
      const formattedMinutes = minutes.toString().padStart(2, '00');
      return `${formattedHours}:${formattedMinutes}`;
    }
    return '';
  };

  return (
    <div className="APMEquipments">
      <Header pageTitle={lang.APM_POS} />

      <FilterBar
        onSearch={loadApmEquipList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          entity: true,
          lid: true,
          posid: true
        }}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.GLOBAL_ENTITY}</Table.Th>
              <Table.Th>POS</Table.Th>
              <Table.Th>{lang.EQUIPMENT_DESCRIPTION}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>{lang.ENTERPRISE_EQUIP_PERIODS_DT_CLOSE}</Table.Th>
              <Table.Th>{lang.GLOBAL_EQUIPMENT}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_STATE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_COM_DATE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_LAST_OPER}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_LAST_OPER_DT}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((pos, index) => {
                    return (
                      <Table.Tr key={index}>
                        <Table.Td>{handleEntid(entities, pos.pos.entid)}</Table.Td>
                        <Table.Td>{pos.pos.posid}</Table.Td>
                        <Table.Td>{pos.pos.description}</Table.Td>
                        <Table.Td>{getLocationName(pos.pos.sid)}</Table.Td>
                        <Table.Td>{checkTimeOpenCloseforCell(pos.pos.timeopenclose)}</Table.Td>
                        <Table.Td>{checkEquipmentEid(pos.pos.eid)}</Table.Td>
                        <Table.Td>{checkStatusForCell(pos.status)}</Table.Td>
                        <Table.Td>{formatDateTimeLabel(pos.lastComDt, region)}</Table.Td>
                        <Table.Td>{formatShortTransactionType(lang, pos.lastOperation)}</Table.Td>
                        <Table.Td>{formatDateTimeLabel(pos.lastOperationDt, region)}</Table.Td>
                        <Table.Td>
                          <EditEquipmentPos
                            posid={pos.pos.posid}
                            timeopenclose={pos.pos.timeopenclose || 0}
                            description={pos.pos.description}
                            update={loadApmEquipList}
                          />
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default APMEquipments;
